import {makeCresAxiosFoundationAPI} from "./AxiosFoundationAPI";
import {ObjectStatistics} from "./objectStatistics";

/**
 * The change order lane, as the filter sidebar's rows.
 *
 * ITS OWN VOCABULARY, NOT THE RECORD ONE. A change order is not an OBJECT_REVISION and shares none
 * of its terminal states: there is no Released, Deprecated or Superseded, and IMPLEMENTATION -- signed
 * with work still outstanding -- has no counterpart at all. Reusing OBJECT_STATUSES would offer six
 * rows of which four could never match and hide the one state a reader most needs to find.
 *
 * ORDERED BY THE LIFECYCLE, not alphabetically, so the column reads as the path a change order takes.
 */
export const CHANGE_ORDER_STATES: {label: string; status: string}[] = [
    {label: "Draft", status: "DRAFT"},
    {label: "In review", status: "IN_REVIEW"},
    {label: "Approved", status: "APPROVED"},
    {label: "Implementation", status: "IMPLEMENTATION"},
    {label: "Closed", status: "CLOSED"},
    {label: "Voided", status: "VOIDED"},
];

/** What a change order list opens showing: the ones still moving. Closed and voided are history. */
export const OPEN_CHANGE_ORDER_STATES = ["DRAFT", "IN_REVIEW", "APPROVED", "IMPLEMENTATION"];

function countOf(response: any): number {
    const group = response?.data?.PlatformMessage?.ListOfChangeOrder;
    const count = group?.Count;
    return typeof count === "number" ? count : Number(count ?? 0) || 0;
}

/**
 * Load the sidebar counts. Never rejects: a statistics panel that breaks the page it decorates is
 * worse than one showing dashes, so a failed request resolves to null and the caller falls back.
 *
 * COUNTED UNDER THE ACTIVE PERSONA, by sending the same visibility headers the grid sends. A count
 * computed unscoped beside a list that is scoped would tell the reader there are eleven drafts and
 * then show them two.
 */
export async function loadChangeOrderStatistics(sessionContext: any): Promise<ObjectStatistics | null> {
    try {
        const api = makeCresAxiosFoundationAPI(sessionContext);
        const active = sessionContext?.getActiveVisibility();
        const headers = {
            "visibility-mode": active?.Mode ?? "",
            "visibility-value": active?.Value ?? "",
        };

        const requests = CHANGE_ORDER_STATES.map(s =>
            api.get("t1/v1/change_orders", {params: {State: s.status, Limit: 1}, headers})
                .then(r => ({label: s.label, status: s.status, count: countOf(r)}))
                // One failed state must not lose the other five.
                .catch(() => ({label: s.label, status: s.status, count: 0})),
        );

        // NO TYPES, and not because they were not fetched: a change order is not an OBJECT_TYPE'd
        // record at all. The empty list is the fact, and it is what keeps the sidebar's Type section
        // off this page without the page having to ask for it to be hidden.
        return {statuses: await Promise.all(requests), types: []};
    } catch (e) {
        console.error("failed to load change order statistics", e);
        return null;
    }
}
